/** Classify command-bar text as portfolio insight vs actionable workflow. */

/** PE / lite report workflow phrases — must not be classified as insight questions. */
export function isReportWorkflowPhrase(text) {
  const t = (text || "").trim().toLowerCase();
  if (!t) return false;
  if (/\blite\s+report\b/.test(t)) return true;
  if (/\bopen\s+lite\b/.test(t)) return true;
  if (/\b(?:pe\s+)?portfolio\s+(?:report|roll-?up)\b/.test(t)) return true;
  if (/\bopen\s+(?:pe\s+)?portfolio\b/.test(t)) return true;
  if (/\bpe\s+portfolio\b/.test(t)) return true;
  if (/\bportfolio\s+report\b/.test(t)) return true;
  if (/\b(?:pe|private\s+equity)\s+(?:portfolio\s+)?report\b/.test(t)) return true;
  return false;
}

export function looksLikeInsightQuestion(text) {
  if (isReportWorkflowPhrase(text)) return false;

  const t = (text || "").trim().toLowerCase();
  if (!t) return false;

  if (/\?\s*$/.test((text || "").trim())) return true;

  if (
    /^(what|which|who|how|why|when|where|tell me|list|show me|give me|are there|is there|do we|can you|could you)\b/.test(
      t
    )
  ) {
    return true;
  }

  if (/\b(report[- ]?ready|how many|how much|average|median|completion rate)\b/.test(t)) {
    return true;
  }

  if (/\bportfolio\b/.test(t) && !/\b(portfolio\s+report|portfolio\s+roll-?up|pe\s+portfolio|open\s+portfolio)\b/.test(t)) {
    return true;
  }

  if (/\b(which companies|companies with|companies that|compare|trend|overview of)\b/.test(t)) {
    return true;
  }

  return false;
}

/** Whole-message replies that approve a pending workflow step. */
export function isAffirmativeConfirm(text) {
  return /^\s*(yes|yeah|yep|confirm|confirmed|proceed|go ahead|ok|okay|sure|execute|send it|do it)\s*[.!]?\s*$/i.test(
    text || ""
  );
}

/** Confirm chip for reminder emails, not a new invite batch. */
export function isReminderConfirmAction(action) {
  if (!action || action.type !== "confirm") return false;
  const flow = String(action.flow_kind || action.flowKind || "").toLowerCase();
  if (flow === "resend_opened_incomplete") return true;
  if (flow === "send_invites") return false;
  const blob = `${action.label || ""} ${action.reason || ""}`.toLowerCase();
  return /\bremind/.test(blob) || /\bresend\b/.test(blob);
}

/** Latest assistant turn is waiting for a reminder-email confirmation. */
export function threadAwaitsReminderConfirm(messages = []) {
  for (let i = (messages || []).length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === "user") continue;
    if (m?.role !== "assistant") continue;
    if (m?.meta?.kind === "workflow_start") continue;

    const flow = String(
      m?.structured?.workflow?.flow_kind || m?.meta?.flowKind || ""
    ).toLowerCase();
    if (flow === "resend_opened_incomplete") return true;
    if (flow === "send_invites") return false;

    const actions = m?.structured?.actions || [];
    if (actions.some((action) => isReminderConfirmAction(action))) return true;

    const blob = `${m?.content || ""} ${m?.structured?.summary || ""}`.toLowerCase();
    if (
      blob.includes("reply confirm to send reminder") ||
      blob.includes("opened their invite but have not completed")
    ) {
      return true;
    }
    if (blob.includes("couldn't find any email addresses") || blob.includes("who should receive invites")) {
      continue;
    }
    return false;
  }
  return false;
}

export function looksLikeWorkflow(text) {
  if (isReportWorkflowPhrase(text)) return true;

  if (looksLikeInsightQuestion(text)) return false;

  const t = (text || "").trim().toLowerCase();
  if (!t) return false;
  if (t.includes("@")) return true;

  if (/\b(invite|invites|invitation|onboarding)\b/.test(t)) return true;

  if (
    /\bdraft(?:\s+a)?\s+reminders?\b/.test(t) ||
    /\bdraft\s+reminder\s+emails?\b/.test(t) ||
    /\breminders?\s+for\s+those\b/.test(t)
  ) {
    return true;
  }

  if (
    /\b(open|show|bring\s+up|pull\s+up|generate|get)\s+(?:the\s+)?(?:report|summary|pdf)\b/.test(t)
  ) {
    return true;
  }

  if (
    /\b(executive\s+summary|exec\s+summary|team\s+report|company\s+report|resilience\s+report|lite\s+report)\b/.test(
      t
    )
  ) {
    return true;
  }

  if (/\bpe\s+portfolio\b/.test(t)) return true;

  if (/\breport\s+for\b/.test(t) || /\bpdf\s+for\b/.test(t)) return true;

  if (/\bsend\s+.+\s+(?:an?\s+)?(?:invite|invitation|email)\b/.test(t)) return true;

  if (
    /\b(?:create|build|configure|make|new|design)\s+(?:a\s+)?(?:report\s+)?profile\b/.test(t)
  ) {
    return true;
  }

  if (/\breport\s+profile\s+(?:with|for)\b/.test(t)) return true;

  if (/\bblocks?\s+in\s+order\b/.test(t)) return true;

  if (/\bsuggest\s+blocks?\b/.test(t)) return true;

  return false;
}

/** Map free-text command to a workflow guide id when starting from the command bar. */
export function inferWorkflowGuideId(text) {
  const t = (text || "").trim().toLowerCase();
  if (!t) return null;
  if (
    /\bdraft(?:\s+a)?\s+reminders?\b/.test(t) ||
    /\bdraft\s+reminder\s+emails?\b/.test(t) ||
    /\breminders?\s+for\s+those\b/.test(t) ||
    /\bsend\s+reminder\s+emails?\b/.test(t) ||
    /\breminder\s+emails?\b/.test(t)
  ) {
    return "reminder";
  }
  if (/\b(invite|invites|invitation|onboarding)\b/.test(t) || t.includes("@")) return "invite";
  if (/\bexecutive\s+summary\b/.test(t) || /\bexec\s+summary\b/.test(t)) return "exec";
  if (/\bteam\s+report\b/.test(t)) return "team";
  if (
    /\bpe\s+portfolio\b/.test(t) ||
    /\bportfolio\s+(?:report|roll-?up)\b/.test(t) ||
    /\bopen\s+(?:pe\s+)?portfolio\b/.test(t) ||
    /\b(?:pe|private\s+equity)\s+(?:portfolio\s+)?report\b/.test(t)
  ) {
    return "pe";
  }
  if (/\blite\s+report\b/.test(t) || /\bopen\s+lite\b/.test(t)) return "lite";
  if (/\bcompany\s+report\b/.test(t)) return "company";
  if (/\breport\s+profile\b/.test(t) || /\b(?:create|build|configure)\s+(?:a\s+)?(?:report\s+)?profile\b/.test(t)) {
    return "profile";
  }
  if (/\b(open|show|generate|get)\s+(?:the\s+)?(?:report|summary|pdf)\b/.test(t)) return "company";
  return null;
}

/** Infer active report workflow from recent thread context (e.g. after platform clarify chips). */
export function inferWorkflowGuideFromThread(messages = []) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    const workflowId = m?.meta?.workflowId;
    if (workflowId && WORKFLOW_GUIDE_ID_SET.has(workflowId)) return workflowId;
    const flowKind = String(
      m?.structured?.workflow?.flow_kind || m?.meta?.flowKind || m?.meta?.workflowAction || ""
    ).toLowerCase();
    if (flowKind === "resend_opened_incomplete") return "reminder";
    if (flowKind === "send_invites") return "invite";
    if (flowKind === "open_report") {
      return "company";
    }
    const blob = `${m?.content || ""} ${m?.structured?.summary || ""}`.toLowerCase();
    if (
      blob.includes("reply confirm to send reminder") ||
      blob.includes("opened their invite but have not completed") ||
      blob.includes("draft reminder") ||
      blob.includes("send reminder emails")
    ) {
      return "reminder";
    }
    if (blob.includes("executive summary") || blob.includes("exec summary")) return "exec";
    if (blob.includes("team report")) return "team";
    if (blob.includes("company report")) return "company";
    if (blob.includes("lite report") || blob.includes("lite company report")) return "lite";
    if (
      blob.includes("portfolio report") ||
      blob.includes("portfolio roll-up") ||
      blob.includes("pe portfolio")
    ) {
      return "pe";
    }
    if (blob.includes("report profile")) return "profile";
    if (
      blob.includes("who should receive invites") ||
      blob.includes("participant email") ||
      (/\binvites?\b/.test(blob) && !/\breminder\b/.test(blob) && !blob.includes("opened their invite"))
    ) {
      return "invite";
    }
  }
  return null;
}

export const WORKFLOW_GUIDE_IDS = ["invite", "reminder", "exec", "team", "company", "profile", "lite", "pe"];
const WORKFLOW_GUIDE_ID_SET = new Set(WORKFLOW_GUIDE_IDS);
